import { FC } from 'react'
import dynamic from 'next/dynamic'

import { PageBuilderSection } from '@portfolio/models'
import { SectionIntroTexts } from './SectionIntroTexts/SectionIntroTexts'
import { SectionArticlesLandingPage } from './SectionArticlesLandingPage/SectionArticlesLandingPage'
import { SectionArticleDetails } from './SectionArticleDetails/SectionArticleDetails'
import { SectionCaseStudyHero } from './SectionCaseStudyHero/SectionCaseStudyHero'
import { SectionProjectCardGrid } from './SectionProjectCardGrid/SectionProjectCardGrid'
import { SectionHomeHero } from './SectionHomeHero/SectionHomeHero'
import { SectionProjectCard } from './SectionProjectCard/SectionProjectCard'
import { SectionContact } from './SectionContact/SectionContact'
import { SectionSideTextBlock } from './SectionSideTextBlock/SectionSideTextBlock'
import { SectionServices } from './SectionServices/SectionServices'
import { SectionArticleContents } from './SectionArticleContents/SectionArticleContents'
import { SectionArticleRelated } from './SectionRelatedArticles/SectionRelatedArticles'
import { SectionHero } from './SectionHero/SectionHero'

const DynamicSectionEditorialRichText = dynamic(() =>
  import('./SectionEditorialRichText/SectionEditorialRichText').then(
    (mod) => mod.SectionEditorialRichText,
  ),
)

const DynamicSectionQuote = dynamic(() =>
  import('./SectionQuote/SectionQuote').then((mod) => mod.SectionQuote),
)

const DynamicSectionCardSlider = dynamic(() =>
  import('./SectionCardSlider/SectionCardSlider').then(
    (mod) => mod.SectionCardSlider,
  ),
)

const DynamicSectionNextProject = dynamic(() =>
  import('./SectionNextProject/SectionNextProject').then(
    (mod) => mod.SectionNextProject,
  ),
)

const DynamicSectionCaseStudyDetails = dynamic(() =>
  import('./SectionCaseStudyDetails/SectionCaseStudyDetails').then(
    (mod) => mod.SectionCaseStudyDetails,
  ),
)

const DynamicSectionTextBlockWithLink = dynamic(() =>
  import('./SectionTextBlockWithLink/SectionTextBlockWithLink').then(
    (mod) => mod.SectionTextBlockWithLink,
  ),
)

const DynamicSectionTextBlocks = dynamic(() =>
  import('./SectionTextBlocks/SectionTextBlocks').then(
    (mod) => mod.SectionTextBlocks,
  ),
)

const DynamicSectionVideoTestimonials = dynamic(() =>
  import('./SectionVideoTestimonials/SectionVideoTestimonials').then(
    (mod) => mod.SectionVideoTestimonials,
  ),
)

const DynamicSectionSingleMediaWithDescription = dynamic(() =>
  import(
    './SectionSingleMediaWithDescription/SectionSingleMediaWithDescription'
  ).then((mod) => mod.SectionSingleMediaWithDescription),
)

const DynamicSectionArticleChapter = dynamic(() =>
  import('./SectionArticleChapter/SectionArticleChapter').then(
    (mod) => mod.SectionArticleChapter,
  ),
)

const DynamicSectionArticleAuthor = dynamic(() =>
  import('./SectionArticleAuthor/SectionArticleAuthor').then(
    (mod) => mod.SectionArticleAuthor,
  ),
)

const DynamicSectionDoubleMediaWithBackground = dynamic(() =>
  import(
    './SectionDoubleMediaWithBackground/SectionDoubleMediaWithBackground'
  ).then((mod) => mod.SectionDoubleMediaWithBackground),
)

const DynamicSectionCaseStudyCards = dynamic(() =>
  import('./SectionCaseStudyCards/SectionCaseStudyCards').then(
    (mod) => mod.SectionCaseStudyCards,
  ),
)

const DynamicSectionServicesListing = dynamic(() =>
  import('./SectionServicesListing/SectionServicesListing').then(
    (mod) => mod.SectionServicesListing,
  ),
)

const DynamicSectionTechstackSlider = dynamic(() =>
  import('./SectionTechstackSlider/SectionTechstackSlider').then(
    (mod) => mod.SectionTechstackSlider,
  ),
)

const DynamicSectionNewsletter = dynamic(() =>
  import('./SectionNewsletter/SectionNewsletter').then(
    (mod) => mod.SectionNewsletter,
  ),
)

const DynamicSectionSocialMediaEmbed = dynamic(() =>
  import('./SectionSocialMediaEmbed/SectionSocialMediaEmbed').then(
    (mod) => mod.SectionSocialMediaEmbed,
  ),
)

const DynamicSectionFeaturedBlogPosts = dynamic(() =>
  import('./SectionFeaturedBlogPosts/SectionFeaturedBlogPosts').then(
    (mod) => mod.SectionFeaturedBlogPosts,
  ),
)

const DynamicSectionTermly = dynamic(() =>
  import('./SectionTermly/SectionTermly').then((mod) => mod.SectionTermly),
)

const DynamicSectionNewsletterV2 = dynamic(() =>
  import('./SectionNewsletterV2/SectionNewsletterV2').then(
    (mod) => mod.SectionNewsletterV2,
  ),
)

const DynamicSectionShouldIUseHydrogen = dynamic(() =>
  import('./SectionShouldIUseHydrogen/SectionShouldIUseHydrogen').then(
    (mod) => mod.SectionShouldIUseHydrogen,
  ),
)

const DynamicSectionBanner = dynamic(() =>
  import('./SectionBanner/SectionBanner').then((mod) => mod.SectionBanner),
)

const DynamicSectionSideContent = dynamic(() =>
  import('./SectionSideContent/SectionSideContent').then(
    (mod) => mod.SectionSideContent,
  ),
)

const DynamicSectionCards = dynamic(() =>
  import('./SectionCards/SectionCards').then((mod) => mod.SectionCards),
)

const DynamicSectionCallToAction = dynamic(() =>
  import('./SectionCallToAction').then((mod) => mod.SectionCallToAction),
)

const DynamicSectionOpenPositions = dynamic(() =>
  import('./SectionOpenPositions/SectionOpenPositions').then(
    (mod) => mod.SectionOpenPositions,
  ),
)

const DynamicSectionSteps = dynamic(() =>
  import('./SectionSteps/SectionSteps').then((mod) => mod.SectionSteps),
)

const DynamicSectionProcessSteps = dynamic(() =>
  import('./SectionProcessSteps/SectionProcessSteps').then(
    (mod) => mod.SectionProcessSteps,
  ),
)

const DynamicSectionAccordion = dynamic(() =>
  import('./SectionAccordion/SectionAccordion').then(
    (mod) => mod.SectionAccordion,
  ),
)

const DynamicSectionMasonryGallery = dynamic(() =>
  import('./SectionMasonryGallery/SectionMasonryGallery').then(
    (mod) => mod.SectionMasonryGallery,
  ),
)

const DynamicSectionAwardArticles = dynamic(() =>
  import('./SectionAwardArticles/SectionAwardArticles').then(
    (mod) => mod.SectionAwardArticles,
  ),
)

const DynamicSectionPeople = dynamic(() =>
  import('./SectionPeople/SectionPeople').then((mod) => mod.SectionPeople),
)

const DynamicSectionSideContentWithMedia = dynamic(() =>
  import('./SectionSideContentWithMedia/SectionSideContentWithMedia').then(
    (mod) => mod.SectionSideContentWithMedia,
  ),
)

const DynamicSectionBlogHighlights = dynamic(() =>
  import('./SectionBlogHighlights/SectionBlogHighlights').then(
    (mod) => mod.SectionBlogHighlights,
  ),
)

const DynamicSectionLogosWithTestimonials = dynamic(() =>
  import('./SectionLogosWithTestimonials/SectionLogosWithTestimonials').then(
    (mod) => mod.SectionLogosWithTestimonials,
  ),
)

const DynamicSectionCTAButton = dynamic(() =>
  import('./SectionCTAButton/SectionCTAButton').then(
    (mod) => mod.SectionCTAButton,
  ),
)

const DynamicSectionHighlights = dynamic(() =>
  import('./SectionHighlights/SectionHighlights').then(
    (mod) => mod.SectionHighlights,
  ),
)

const DynamicSectionLogos = dynamic(() =>
  import('./SectionLogos/SectionLogos').then((mod) => mod.SectionLogos),
)

export type PageBuilderProps = {
  section: PageBuilderSection
}

export const PageBuilder: FC<PageBuilderProps> = ({ section }) => {
  /**
   * We use two section import strategies:
   * - Dynamic - in order to minimise the size of bundle chunks, we prefer to import sections dynamically
   * - Static - to prevent the "blink" effect in most exposed sections we use static imports
   */

  switch (section._type) {
    // dynamic sections ======================================
    case 'sectionHighlights':
      return <DynamicSectionHighlights {...section} />

    case 'sectionBlogHighlights':
      return <DynamicSectionBlogHighlights {...section} />

    case 'sectionLogosWithTestimonials':
      return <DynamicSectionLogosWithTestimonials {...section} />

    case 'sectionSideContentWithMedia':
      return <DynamicSectionSideContentWithMedia {...section} />

    case 'sectionAwardArticles':
      return <DynamicSectionAwardArticles {...section} />

    case 'sectionProcessSteps':
      return <DynamicSectionProcessSteps {...section} />
    case 'sectionMasonryGallery':
      return <DynamicSectionMasonryGallery {...section} />

    case 'sectionAccordion':
      return <DynamicSectionAccordion {...section} />

    case 'sectionSteps':
      return <DynamicSectionSteps {...section} />

    case 'sectionShouldIUseHydrogen':
      return <DynamicSectionShouldIUseHydrogen {...section} />

    case 'sectionBanner':
      return <DynamicSectionBanner {...section} />

    case 'sectionSideContent':
      return <DynamicSectionSideContent {...section} />

    case 'sectionCards':
      return <DynamicSectionCards {...section} />

    case 'sectionCallToAction':
      return <DynamicSectionCallToAction {...section} />

    case 'sectionNewsletterV2':
      return <DynamicSectionNewsletterV2 {...section} />

    case 'sectionTermly':
      return <DynamicSectionTermly {...section} />

    case 'sectionFeaturedBlogPosts':
      return <DynamicSectionFeaturedBlogPosts {...section} />

    case 'sectionSocialMediaEmbed':
      return <DynamicSectionSocialMediaEmbed {...section} />

    case 'sectionNewsletter':
      return <DynamicSectionNewsletter {...section} />

    case 'sectionDoubleMediaWithBackground':
      return <DynamicSectionDoubleMediaWithBackground {...section} />

    case 'sectionArticleAuthor':
      return <DynamicSectionArticleAuthor {...section} />

    case 'sectionArticleChapter':
      return <DynamicSectionArticleChapter {...section} />

    case 'sectionSingleMediaWithDescription':
      return <DynamicSectionSingleMediaWithDescription {...section} />

    case 'sectionVideoTestimonials':
      return <DynamicSectionVideoTestimonials {...section} />

    case 'sectionTextBlocks':
      return <DynamicSectionTextBlocks {...section} />

    case 'sectionTextBlockWithLink':
      return <DynamicSectionTextBlockWithLink {...section} />

    case 'sectionCaseStudyDetails':
      return <DynamicSectionCaseStudyDetails {...section} />

    case 'sectionNextProject':
      return <DynamicSectionNextProject {...section} />

    case 'sectionCardSlider':
      return <DynamicSectionCardSlider {...section} />

    case 'sectionQuote':
      return <DynamicSectionQuote {...section} />

    case 'sectionEditorialRichText':
      return <DynamicSectionEditorialRichText {...section} />

    case 'sectionCaseStudyCards':
      return <DynamicSectionCaseStudyCards {...section} />

    case 'sectionServicesListing':
      return <DynamicSectionServicesListing {...section} />

    case 'sectionTechstackSlider':
      return <DynamicSectionTechstackSlider {...section} />

    case 'sectionOpenPositions':
      return <DynamicSectionOpenPositions {...section} />

    case 'sectionPeople':
      return <DynamicSectionPeople {...section} />

    case 'sectionCTAButton':
      return <DynamicSectionCTAButton {...section} />

    case 'sectionLogos':
      return <DynamicSectionLogos {...section} />

    // static sections ==================================
    case 'sectionHero':
      return <SectionHero {...section} />

    case 'sectionIntroTexts':
      return <SectionIntroTexts {...section} />

    case 'sectionCaseStudyHero':
      return <SectionCaseStudyHero {...section} />

    case 'sectionProjectCardGrid':
      return <SectionProjectCardGrid {...section} />

    case 'sectionHomeHero':
      return <SectionHomeHero {...section} />

    case 'sectionProjectCard':
      return <SectionProjectCard {...section} />

    case 'sectionArticleDetails':
      return <SectionArticleDetails {...section} />

    case 'sectionArticlesLandingPage':
      return <SectionArticlesLandingPage {...section} />

    case 'sectionContact':
      return <SectionContact {...section} />

    case 'sectionSideTextBlock':
      return <SectionSideTextBlock {...section} />

    case 'sectionServices':
      return <SectionServices {...section} />

    case 'sectionArticleContents':
      return <SectionArticleContents {...section} />

    case 'sectionArticleRelated':
      return <SectionArticleRelated {...section} />

    default:
      return null
  }
}
